import {
  CHART_CANVAS_WIDTH,
  CHART_INNER_HEIGHT,
  CHART_PADDING_X,
  CHART_TOP,
  CURRENT_BAR_WIDTH,
  GROUP_GAP,
  MIN_MOVEMENT_BAR_HEIGHT,
  MOVEMENT_BAR_COUNT,
  MOVEMENT_BAR_GAP,
  MOVEMENT_BAR_WIDTH,
  PREVIOUS_BAR_WIDTH,
  getBarHeight
} from './comparisonBarChartConstants'

export interface TrendPoint {
  x: number
  y: number
}

interface ChartGeometryInput {
  currentValue: number
  previousValue: number
  maxValue: number
}

interface ChartGeometry {
  movementHeights: number[]
  hasMovementBars: boolean
  movementLeft: number
  previousCenterX: number
  currentCenterX: number
  trendPoints: TrendPoint[]
}

const calculateMovementHeights = (previousHeight: number, currentHeight: number): number[] => {
  return Array.from({ length: MOVEMENT_BAR_COUNT }).map((_, index) => {
    const progress = (index + 1) / MOVEMENT_BAR_COUNT
    const interpolatedHeight = previousHeight + (currentHeight - previousHeight) * progress

    if (interpolatedHeight <= 0) {
      return 0
    }

    return Math.max(interpolatedHeight, MIN_MOVEMENT_BAR_HEIGHT)
  })
}

const midpointLift = (currentValue: number, previousValue: number) => (currentValue >= previousValue ? 14 : 8)

const toChartY = (height: number) => CHART_TOP + (CHART_INNER_HEIGHT - height)

export const calculateChartGeometry = ({
  currentValue,
  previousValue,
  maxValue
}: ChartGeometryInput): ChartGeometry => {
  const previousHeight = getBarHeight(previousValue, maxValue)
  const currentHeight = getBarHeight(currentValue, maxValue)

  const movementHeights = previousValue <= 0 ? [] : calculateMovementHeights(previousHeight, currentHeight)

  const hasMovementBars = previousValue > 0 && movementHeights.some((height) => height > 0)
  const movementBarsWidth = hasMovementBars
    ? MOVEMENT_BAR_COUNT * MOVEMENT_BAR_WIDTH + (MOVEMENT_BAR_COUNT - 1) * MOVEMENT_BAR_GAP
    : 0

  const previousCenterX = CHART_PADDING_X + PREVIOUS_BAR_WIDTH / 2
  const movementLeft = CHART_PADDING_X + PREVIOUS_BAR_WIDTH + GROUP_GAP
  const currentLeft = CHART_CANVAS_WIDTH - CHART_PADDING_X - CURRENT_BAR_WIDTH
  const currentCenterX = currentLeft + CURRENT_BAR_WIDTH / 2
  const movementCenterX = hasMovementBars
    ? movementLeft + movementBarsWidth / 2
    : (previousCenterX + currentCenterX) / 2

  const previousY = toChartY(previousHeight)
  const currentY = toChartY(currentHeight)
  const lift = midpointLift(currentValue, previousValue)
  const midpointY = Math.min(previousY, currentY) - lift

  const trendPoints: TrendPoint[] =
    previousValue <= 0
      ? []
      : [
          { x: previousCenterX, y: previousY },
          { x: movementCenterX, y: Math.max(midpointY, CHART_TOP) },
          { x: currentCenterX, y: currentY }
        ]

  return {
    movementHeights,
    hasMovementBars,
    movementLeft,
    previousCenterX,
    currentCenterX,
    trendPoints
  }
}
